<template>
    <div class="cart-item-wrapper mb-6">
        <!-- Main Card Container -->
        <div class="cart-item-card bg-white rounded-xl border-2 border-[var(--bone-dark)] overflow-hidden transition-all duration-300 hover:border-[var(--primary)]">
            
            <!-- Card Header with Tour Info and Actions -->
            <div class="card-header bg-gradient-to-r from-[var(--primary-light)] to-[var(--bone)] p-4 sm:p-5 border-b border-[var(--bone-dark)]">
                <div class="min-[480px]:flex items-start justify-between gap-3">
                    <!-- Action Buttons -->
                    <div class="flex items-center gap-2 flex-shrink-0 min-[480px]:order-2 max-[479px]:mb-2 max-[479px]:justify-end">
                        <button 
                            class="action-btn edit-btn w-9 h-9 sm:w-10 sm:h-10 bg-[var(--charcoal)] rounded-lg flex items-center justify-center hover:bg-[var(--charcoal-dark)] transition-all transform hover:scale-105" 
                            @click="$emit('edit-package', item)" 
                            title="Edit Tour">
                            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </button>
                        <button 
                            v-if="firstPackageId !== item.package_id" 
                            class="action-btn delete-btn w-9 h-9 sm:w-10 sm:h-10 bg-[var(--tone-gray)] rounded-lg flex items-center justify-center hover:bg-[var(--charcoal)] transition-all transform hover:scale-105" 
                            @click="$emit('remove-from-cart', item)" 
                            title="Remove Tour">
                            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </button>
                        <button 
                            class="action-btn checkout-btn w-9 h-9 sm:w-10 sm:h-10 bg-[var(--primary)] rounded-lg flex items-center justify-center hover:bg-[var(--primary-dark)] transition-all transform hover:scale-105" 
                            @click="$emit('tab-change', 5, 'Checkout')" 
                            title="Proceed to Checkout">
                            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                                <path d="m5 12 5 5L20 7"></path>
                            </svg>
                        </button>
                    </div>

                    <div class="flex-1">
                        <div class="flex items-start gap-3">
                            <!-- Tour Badge Icon -->
                            <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-[var(--primary)] flex items-center justify-center">
                                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"></path>
                                </svg>
                            </div>
                            <div class="flex-1">
                                <h3 class="text-xl sm:text-2xl font-bold text-[var(--charcoal-dark)] leading-tight mb-1">
                                    {{ item.package_name || 'Unnamed Package' }}
                                </h3>
                                <div v-if="item.duration || item.type" class="inline-flex items-center gap-2 px-3 py-1 bg-white bg-opacity-80 border border-[var(--bone-dark)] rounded-lg">
                                    <svg class="w-4 h-4 text-[var(--info)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                    <span class="text-sm font-semibold text-[var(--charcoal)]">
                                        {{ item.duration || '' }} {{ item.type || '' }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Card Body -->
            <div class="card-body p-4 sm:p-6">
                <div class="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
                    
                    <!-- Tour Image -->
                    <div class="lg:col-span-3">
                        <div class="image-container relative rounded-lg overflow-hidden bg-gradient-to-br from-[var(--bone)] to-[var(--light-gray)] border border-[var(--bone-dark)] h-48 sm:h-56">
                            <img 
                                v-if="item.package_image" 
                                :src="item.package_image" 
                                :alt="`${item.package_name || 'Package'} image`" 
                                class="w-full h-full object-cover transform-gpu transition-transform duration-300 hover:scale-105">
                            <div v-else class="absolute inset-0 flex items-center justify-center flex-col p-4">
                                <svg class="w-16 h-16 text-[var(--tone-gray)] opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                    <line x1="12" y1="22.08" x2="12" y2="12"></line>
                                </svg>
                                <p class="text-xs text-[var(--tone-gray)] uppercase tracking-wide mt-2 font-medium">Image Coming Soon</p>
                            </div>
                        </div>
                    </div>

                    <!-- Tour Details Section -->
                    <div class="lg:col-span-9">
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                            
                            <!-- Date & Time Card -->
                            <div class="detail-card">
                                <div class="card-inner bg-[linear-gradient(111.97deg,_#F4ECE1_0%,_#F5F3F0_100%)] rounded-lg border border-[var(--bone-dark)] p-4 h-full">
                                    <div class="mb-3">
                                        <h4 class="text-sm font-bold text-[var(--charcoal-dark)] uppercase tracking-wide">Schedule</h4>
                                    </div>
                                    <div class="space-y-2">
                                        <div class="flex items-start gap-2">
                                            <svg class="w-4 h-4 text-[var(--charcoal)] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                            </svg>
                                            <div class="flex-1">
                                                <div class="text-xs text-[var(--tone-gray)] font-medium mb-0.5">Date</div>
                                                <div class="text-sm font-semibold text-[var(--charcoal-dark)]">{{ dateFormat(item.date) }}</div>
                                            </div>
                                        </div>
                                        <div class="flex items-start gap-2">
                                            <svg class="w-4 h-4 text-[var(--charcoal)] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <polyline points="12 6 12 12 16 14"></polyline>
                                            </svg>
                                            <div class="flex-1">
                                                <div class="text-xs text-[var(--tone-gray)] font-medium mb-0.5">Time</div>
                                                <div class="text-sm font-semibold text-[var(--charcoal-dark)]">{{ item.time_date || 'Not specified' }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Guests Card -->
                            <div class="detail-card">
                                <div class="card-inner bg-[linear-gradient(111.97deg,_#F4ECE1_0%,_#F5F3F0_100%)] rounded-lg border border-[var(--bone-dark)] p-4 h-full">
                                    <div class="mb-3">
                                        <h4 class="text-sm font-bold text-[var(--charcoal-dark)] uppercase tracking-wide">Guests</h4>
                                    </div>
                                    <div class="space-y-2">
                                        <div v-if="item.people_group && typeof item.people_group === 'object' && Object.keys(item.people_group).length">
                                            <div v-for="(count, groupType) in item.people_group" :key="groupType" class="flex items-center justify-between">
                                                <div class="flex items-center gap-2">
                                                    <svg class="w-4 h-4 text-[var(--charcoal)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                                                        <circle cx="12" cy="8" r="4"></circle>
                                                        <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"></path>
                                                    </svg>
                                                    <span class="text-sm font-medium text-[var(--charcoal)]">{{ item.rate_group?.[groupType] || groupType }}</span>
                                                </div>
                                                <span class="text-sm font-bold text-[var(--charcoal-dark)] bg-white px-2 py-1 rounded mt-1">{{ count }}</span>
                                            </div>
                                        </div>
                                        <div v-else class="text-sm text-[var(--tone-gray)] italic">No guests</div>
                                    </div>
                                </div>
                            </div>

                            <!-- Pricing Card -->
                            <div class="detail-card">
                                <div class="card-inner bg-[linear-gradient(111.97deg,_#F4ECE1_0%,_#F5F3F0_100%)] rounded-lg border border-[var(--bone-dark)] p-4 h-full">
                                    <div class="mb-3">
                                        <h4 class="text-sm font-bold text-[var(--charcoal-dark)] uppercase tracking-wide">Pricing</h4>
                                    </div>
                                    
                                    <div class="space-y-1.5 text-sm">
                                        <!-- Subtotal -->
                                        <div class="flex items-center justify-between text-[var(--charcoal)]">
                                            <span>Subtotal</span>
                                            <span class="font-semibold">{{ currencyFormat(item.subtotal || 0) }}</span>
                                        </div>
                                        
                                        <!-- Discount -->
                                        <div v-if="item?.discount2_value > 0" class="flex items-center justify-between text-[var(--error)]">
                                            <span>Discount 
                                                <span v-if="item?.discount2_percentage" class="text-xs">({{ item?.discount2_percentage }}%)</span>
                                            </span>
                                            <span class="font-semibold">-{{ currencyFormat(item?.discount2_value || 0) }}</span>
                                        </div>
                                        
                                        <!-- Add-ons -->
                                        <div v-if="hasVisibleAddons(item?.custom_fields)" class="border-t border-[var(--bone-dark)] pt-1.5 mt-1.5">
                                            <div v-for="(option, k) in item.custom_fields" :key="`addon-${k}`">
                                                <!-- Parent add-on -->
                                                <div v-if="option.priceInfo?.enabled && option.priceInfo?.subtotal > 0" class="flex items-center justify-between text-xs text-[var(--charcoal)] mb-1">
                                                    <span class="flex items-center gap-1">
                                                        <svg class="w-3 h-3 text-[var(--charcoal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                                                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"></path>
                                                        </svg>
                                                        {{ option.name }}
                                                    </span>
                                                    <span class="font-medium">{{ currencyFormat(option.priceInfo.subtotal) }}</span>
                                                </div>
                                                
                                                <!-- Child add-ons -->
                                                <div v-if="option.children && option.children.length > 0">
                                                    <div v-for="(child, childIdx) in option.children" :key="`child-${k}-${childIdx}`">
                                                        <div v-if="child.priceInfo?.enabled && child.priceInfo?.subtotal > 0" class="flex items-center justify-between text-xs text-[var(--charcoal)] ml-4 mb-1">
                                                            <span class="flex items-center gap-1">
                                                                <svg class="w-3 h-3 text-[var(--tone-gray)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                                                                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"></path>
                                                                </svg>
                                                                {{ child.name }}
                                                            </span>
                                                            <span class="font-medium">{{ currencyFormat(child.priceInfo.subtotal) }}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <!-- Taxes & Fees -->
                                        <div class="flex items-center justify-between text-xs text-[var(--tone-gray)]">
                                            <span>Taxes & Fees</span>
                                            <span class="font-medium">{{ currencyFormat(Number(item.fees || 0) + Number(item.addons_fee || 0)) }}</span>
                                        </div>
                                        
                                        <!-- Total -->
                                        <div class="flex items-center justify-between border-t-2 border-[var(--charcoal-dark)] pt-2 mt-2">
                                            <span class="font-bold text-base text-[var(--charcoal-dark)]">Total</span>
                                            <span class="font-bold text-lg text-[var(--charcoal-dark)]">
                                                {{ currencyFormat(Number(item.total || 0) + Number(item.addons_fee || 0) + Number(item.addons_total || 0)) }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatCurrencyIntl } from '../../utils/currency';
import { getMomentTimezone } from '../../utils/dateUtils';

export default {
    name: 'CartItem',
    props: ['item', 'firstPackageId'],
    emits: ['edit-package', 'remove-from-cart', 'tab-change'],
    methods: {
        dateFormat(date) {
            return getMomentTimezone(this.$store.state.timezone, date).format('dddd, MMMM D, YYYY');
        },
        currencyFormat(amount) {
            return formatCurrencyIntl(amount, this.$store.state.currency);
        },
        hasVisibleAddons(customFields) {
            if (!customFields || !Array.isArray(customFields)) return false;
            
            return customFields.some(field => {
                if (field.priceInfo?.enabled && field.priceInfo?.subtotal > 0) {
                    return true;
                }
                if (field.children && Array.isArray(field.children)) {
                    return field.children.some(child => 
                        child.priceInfo?.enabled && child.priceInfo?.subtotal > 0
                    );
                }
                return false;
            });
        }
    }
};
</script>

<style scoped>
/* Smooth transitions */
img {
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
}

.cart-item-card {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
}

.cart-item-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.action-btn {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-inner {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
</style>
